import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, ArrowLeft, Sparkles, ThumbsUp, SkipForward, Flame, Users, UserPlus, Repeat, Mail, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  water: { label: 'Hydration', emoji: '💧', accent: 'bg-sky-50 text-sky-700 border-sky-100' },
  meals: { label: 'Nutrition', emoji: '🥗', accent: 'bg-amber-50 text-amber-700 border-amber-100' },
  steps: { label: 'Movement', emoji: '🚶', accent: 'bg-violet-50 text-violet-700 border-violet-100' },
  sleep: { label: 'Sleep', emoji: '🌙', accent: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
  maintenance: { label: 'Maintenance', emoji: '✨', accent: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
};

const metaFor = (cat) => CATEGORY_META[cat] || { label: cat, emoji: '•', accent: 'bg-gray-50 text-gray-700 border-gray-100' };

/**
 * Admin-only — Wellness Tip engagement rollup (last 30 days).
 * Renders the data from GET /api/admin/tip-engagement-summary.
 */
const AdminInsights = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [waitlist, setWaitlist] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Rolling window switcher — backend accepts 7, 30, or 90 days only.
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [tipsRes, cohortRes, waitlistRes] = await Promise.all([
          axios.get(`${API}/api/admin/tip-engagement-summary`, {
            params: { window: windowDays },
            withCredentials: true,
          }),
          // Cohort is window-independent (it's a current-state snapshot).
          // Errors on this call must not block the tips section, so we
          // catch and surface a null cohort.
          axios.get(`${API}/api/admin/active-streak-cohort`, {
            withCredentials: true,
          }).catch(() => ({ data: null })),
          // Waitlist insights — window-aware. Same defensive catch as cohort
          // so a single endpoint failure doesn't blank the page.
          axios.get(`${API}/api/admin/waitlist/insights`, {
            params: { window: windowDays },
            withCredentials: true,
          }).catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setData(tipsRes.data);
          setCohort(cohortRes.data);
          setWaitlist(waitlistRes.data);
        }
      } catch (e) {
        if (cancelled) return;
        const status = e.response?.status;
        if (status === 403) setError('Admin access required.');
        else if (status === 401) setError('Please log in as an admin.');
        else setError('Could not load engagement summary.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [windowDays]);

  const maxTotal = useMemo(
    () => Math.max(1, ...(data?.by_category || []).map((c) => c.total)),
    [data],
  );

  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-3">
            <p className="text-lg font-semibold text-gray-900">Admin only</p>
            <p className="text-sm text-gray-600">
              You need an admin account to view this page.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
              data-testid="admin-insights-back-btn"
            >
              ← Back to dashboard
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 pb-20"
      data-testid="admin-insights-page"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
          data-testid="admin-insights-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <header>
          <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" />
            Admin · Wellness Tips
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">
            Tip engagement — last {windowDays} days
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Which categories resonate, and which individual tips users found most useful.
          </p>

          {/* Window switcher: 7 / 30 / 90 days */}
          <div
            role="tablist"
            aria-label="Time window"
            data-testid="admin-insights-window-switcher"
            className="mt-4 inline-flex p-1 rounded-full bg-white border border-gray-200 shadow-sm"
          >
            {[7, 30, 90].map((days) => {
              const active = windowDays === days;
              return (
                <button
                  key={days}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setWindowDays(days)}
                  data-testid={`admin-insights-window-${days}`}
                  className={`px-3.5 h-8 text-xs font-medium rounded-full transition-colors ${
                    active
                      ? 'bg-emerald-600 text-white shadow'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {days}d
                </button>
              );
            })}
          </div>
        </header>

        {loading && (
          <div
            className="flex items-center justify-center py-16"
            data-testid="admin-insights-loading"
          >
            <Loader2 className="h-6 w-6 text-emerald-500 animate-spin" />
          </div>
        )}

        {!loading && error && (
          <Card data-testid="admin-insights-error" className="border-rose-100 bg-rose-50/60">
            <CardContent className="p-5 text-sm text-rose-800">{error}</CardContent>
          </Card>
        )}

        {!loading && !error && data && (
          <>
            {/* Active-streak cohort — longitudinal engagement snapshot */}
            {cohort && (
              <section data-testid="admin-insights-cohort" className="space-y-3">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Active streak cohort
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'total', label: 'Users', value: cohort.total_users, icon: Users, accent: 'text-gray-700' },
                    { key: 'active', label: 'Checked in', value: cohort.active_users, icon: ThumbsUp, accent: 'text-emerald-700' },
                    { key: 'cohort', label: '3+ day streak', value: cohort.cohort_size, icon: Flame, accent: 'text-orange-600' },
                  ].map((s) => {
                    const Icon = s.icon;
                    return (
                      <Card
                        key={s.key}
                        className="border-gray-100 shadow-sm rounded-2xl"
                        data-testid={`admin-insights-cohort-${s.key}`}
                      >
                        <CardContent className="p-4">
                          <div className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${s.accent}`}>
                            <Icon className="h-3.5 w-3.5" /> {s.label}
                          </div>
                          <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">
                            {s.value}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Acquisition vs retention — current snapshot */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    {
                      key: 'new7',
                      label: 'New this week',
                      value: cohort.new_last_7d ?? 0,
                      icon: UserPlus,
                      accent: 'text-sky-700',
                      hint: 'Registered in the last 7 days',
                    },
                    {
                      key: 'returning',
                      label: 'Returning',
                      value: cohort.returning ?? 0,
                      icon: Repeat,
                      accent: 'text-violet-700',
                      hint: 'Checked in 7+ days after signup',
                    },
                  ].map((s) => {
                    const Icon = s.icon;
                    return (
                      <Card
                        key={s.key}
                        className="border-gray-100 shadow-sm rounded-2xl"
                        data-testid={`admin-insights-cohort-${s.key}`}
                        title={s.hint}
                      >
                        <CardContent className="p-4">
                          <div className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${s.accent}`}>
                            <Icon className="h-3.5 w-3.5" /> {s.label}
                          </div>
                          <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">
                            {s.value}
                          </p>
                          <p className="mt-1 text-[11px] text-gray-500">{s.hint}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {cohort.top_streaks.length > 0 && (
                  <Card className="border-gray-100 shadow-sm rounded-2xl">
                    <CardContent className="p-0 divide-y divide-gray-100">
                      {cohort.top_streaks.map((s, i) => (
                        <div
                          key={`${s.name}-${i}`}
                          className="p-3 sm:p-4 flex items-center justify-between gap-3"
                          data-testid={`admin-insights-streak-${i}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold">
                              {i + 1}
                            </span>
                            <span className="text-sm text-gray-900 truncate">{s.name}</span>
                          </div>
                          <span className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-orange-700">
                            <Flame className="h-4 w-4" /> {s.streak}
                          </span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </section>
            )}

            {/* Waitlist demand — preview-feature interest signal */}
            {waitlist && (
              <section data-testid="admin-insights-waitlist" className="space-y-3">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-gray-500" />
                  Waitlist demand
                </h2>

                {/* Top-line metrics */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'total', label: 'Joined', value: waitlist.total ?? 0, icon: Users, accent: 'text-gray-700' },
                    { key: 'new7', label: 'Last 7 days', value: waitlist.new_last_7d ?? 0, icon: UserPlus, accent: 'text-emerald-700' },
                    { key: 'new30', label: 'Last 30 days', value: waitlist.new_last_30d ?? 0, icon: TrendingUp, accent: 'text-sky-700' },
                  ].map((s) => {
                    const Icon = s.icon;
                    return (
                      <Card
                        key={s.key}
                        className="border-gray-100 shadow-sm rounded-2xl"
                        data-testid={`admin-insights-waitlist-${s.key}`}
                      >
                        <CardContent className="p-4">
                          <div className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${s.accent}`}>
                            <Icon className="h-3.5 w-3.5" /> {s.label}
                          </div>
                          <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">
                            {s.value}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Conversion summary — preview views vs. waitlist joins */}
                {(waitlist.total_views ?? 0) > 0 ? (
                  <Card
                    className="border-gray-100 shadow-sm rounded-2xl bg-gradient-to-br from-emerald-50/60 to-white"
                    data-testid="admin-insights-waitlist-conversion-summary"
                  >
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                            Preview → Waitlist conversion
                          </p>
                          <p className="text-2xl font-bold text-gray-900 tabular-nums mt-1">
                            {waitlist.overall_conversion_rate != null
                              ? `${waitlist.overall_conversion_rate}%`
                              : '—'}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            {waitlist.total} of {waitlist.total_views} preview views opted in
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500">Total preview views</p>
                          <p className="text-xl font-semibold text-gray-700 tabular-nums">
                            {waitlist.total_views}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card
                    className="border-dashed border-gray-200 rounded-2xl bg-gray-50/60"
                    data-testid="admin-insights-waitlist-conversion-empty"
                  >
                    <CardContent className="p-4 sm:p-5">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                        Preview → Waitlist conversion
                      </p>
                      <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">
                        Conversion data appears once users start viewing preview screens.
                        Each preview visit is logged anonymously and paired with waitlist
                        opt-ins to show real demand vs. interest.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Daily trend — calm bar chart, no axis labels, soft gradient */}
                {waitlist.trend && waitlist.trend.length > 0 && (
                  <Card className="border-gray-100 shadow-sm rounded-2xl" data-testid="admin-insights-waitlist-trend">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-gray-700">
                          Joins per day · last {waitlist.window_days}d
                        </p>
                        <p className="text-[11px] text-gray-500 tabular-nums">
                          {waitlist.trend.reduce((acc, d) => acc + d.count, 0)} total
                        </p>
                      </div>
                      {(() => {
                        const max = Math.max(1, ...waitlist.trend.map((d) => d.count));
                        return (
                          <div className="flex items-end gap-[2px] h-20" data-testid="admin-insights-waitlist-bars">
                            {waitlist.trend.map((d) => {
                              const h = Math.max(2, Math.round((d.count / max) * 100));
                              const isZero = d.count === 0;
                              return (
                                <div
                                  key={d.day}
                                  className="flex-1 group relative"
                                  title={`${d.day} · ${d.count} join${d.count === 1 ? '' : 's'}`}
                                >
                                  <div
                                    className={`w-full rounded-t-sm transition-all ${
                                      isZero
                                        ? 'bg-gray-100'
                                        : 'bg-gradient-to-t from-emerald-300 to-emerald-500'
                                    }`}
                                    style={{ height: `${h}%` }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      <div className="mt-2 flex items-center justify-between text-[10px] text-gray-400">
                        <span>{waitlist.trend[0]?.day}</span>
                        <span>{waitlist.trend[waitlist.trend.length - 1]?.day}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Top modules — actionable roadmap signal (excludes generic landing) */}
                {waitlist.top_modules && waitlist.top_modules.length > 0 ? (
                  <Card className="border-gray-100 shadow-sm rounded-2xl" data-testid="admin-insights-waitlist-top-modules">
                    <CardContent className="p-0 divide-y divide-gray-100">
                      <div className="p-4 sm:p-5 pb-3">
                        <p className="text-xs font-semibold text-gray-700">Top in-demand modules</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Excludes generic landing-page sign-ups · share is across all sources.
                        </p>
                      </div>
                      {waitlist.top_modules.map((m, i) => (
                        <div
                          key={m.source}
                          className="px-4 sm:px-5 py-3 flex items-center justify-between gap-3"
                          data-testid={`admin-insights-waitlist-module-${i}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                              {i + 1}
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{m.label}</p>
                              <p className="text-[11px] text-gray-500 font-mono truncate">
                                {m.source}
                                {(m.views ?? 0) > 0 && (
                                  <span className="ml-2 text-gray-400">· {m.views} view{m.views === 1 ? '' : 's'}</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-sm font-bold text-gray-900 tabular-nums">{m.count}</p>
                            {m.conversion_rate != null ? (
                              <p className="text-[11px] text-emerald-700 tabular-nums font-medium">
                                {m.conversion_rate}% conv
                              </p>
                            ) : (
                              <p className="text-[11px] text-gray-500 tabular-nums">{m.percentage}%</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-gray-100" data-testid="admin-insights-waitlist-empty">
                    <CardContent className="p-5 text-sm text-gray-500">
                      No preview-module sign-ups yet. Once users tap &ldquo;Notify me&rdquo; on a preview screen,
                      demand by module will appear here.
                    </CardContent>
                  </Card>
                )}

                {/* By source — full breakdown with horizontal bars + conversion */}
                {waitlist.by_source && waitlist.by_source.length > 0 && (
                  <Card className="border-gray-100 shadow-sm rounded-2xl" data-testid="admin-insights-waitlist-by-source">
                    <CardContent className="p-4 sm:p-5">
                      <p className="text-xs font-semibold text-gray-700 mb-3">By source</p>
                      <div className="space-y-3">
                        {(() => {
                          const max = Math.max(1, ...waitlist.by_source.map((s) => s.count));
                          return waitlist.by_source.map((s) => {
                            const widthPct = Math.round((s.count / max) * 100);
                            // Conversion-rate pill colour: green ≥10%, amber ≥3%, grey otherwise.
                            // Threshold is intentionally soft — preview waitlists naturally
                            // convert lower than e.g. landing pages.
                            let convPill = 'bg-gray-100 text-gray-500';
                            if (s.conversion_rate != null) {
                              if (s.conversion_rate >= 10) convPill = 'bg-emerald-100 text-emerald-800';
                              else if (s.conversion_rate >= 3) convPill = 'bg-amber-100 text-amber-800';
                              else convPill = 'bg-rose-50 text-rose-700';
                            }
                            return (
                              <div key={s.source} data-testid={`admin-insights-waitlist-source-${s.source}`}>
                                <div className="flex items-center justify-between gap-3 mb-1">
                                  <p className="text-xs text-gray-700 truncate">{s.label}</p>
                                  <p className="text-[11px] text-gray-500 tabular-nums shrink-0">
                                    {s.count} · {s.percentage}%
                                  </p>
                                </div>
                                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                  <div
                                    className="h-full bg-gradient-to-r from-sky-300 to-sky-500 transition-all duration-700"
                                    style={{ width: `${widthPct}%` }}
                                  />
                                </div>
                                {/* Conversion footer — only render when we have view data */}
                                {(s.views ?? 0) > 0 && (
                                  <div className="mt-1.5 flex items-center justify-between text-[10px] text-gray-500">
                                    <span>{s.views} preview view{s.views === 1 ? '' : 's'}</span>
                                    <span
                                      className={`inline-flex items-center px-1.5 h-4 rounded-full font-semibold ${convPill}`}
                                      data-testid={`admin-insights-waitlist-conversion-${s.source}`}
                                    >
                                      {s.conversion_rate != null ? `${s.conversion_rate}%` : '—'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </section>
            )}

            {/* By category */}
            <section data-testid="admin-insights-by-category" className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                By category
              </h2>
              {data.by_category.length === 0 ? (
                <Card className="border-gray-100">
                  <CardContent className="p-5 text-sm text-gray-500">
                    No engagements yet. Once users start tapping &ldquo;Got it&rdquo; or &ldquo;Next tip&rdquo;,
                    rollups appear here.
                  </CardContent>
                </Card>
              ) : (
                data.by_category.map((row) => {
                  const m = metaFor(row.category);
                  const widthPct = Math.round((row.total / maxTotal) * 100);
                  const rate = row.total > 0 ? Math.round((row.got_it / row.total) * 100) : 0;
                  // Tone the rate pill so high engagement reads green, low reads grey.
                  let ratePillClass = 'bg-gray-100 text-gray-600';
                  if (row.total === 0) ratePillClass = 'bg-gray-100 text-gray-400';
                  else if (rate >= 60) ratePillClass = 'bg-emerald-100 text-emerald-800';
                  else if (rate >= 30) ratePillClass = 'bg-amber-100 text-amber-800';
                  else ratePillClass = 'bg-rose-100 text-rose-800';
                  return (
                    <Card
                      key={row.category}
                      className="border-gray-100 shadow-sm rounded-2xl"
                      data-testid={`admin-insights-category-${row.category}`}
                    >
                      <CardContent className="p-4 sm:p-5">
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-2xl" aria-hidden="true">{m.emoji}</span>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {m.label}
                              </p>
                              <p className="text-xs text-gray-500">
                                {row.total} engagement{row.total === 1 ? '' : 's'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-3 text-xs">
                            <span
                              className={`inline-flex items-center px-2 h-6 rounded-full text-[11px] font-semibold ${ratePillClass}`}
                              title={`${row.got_it} acked out of ${row.total} interactions`}
                              data-testid={`admin-insights-rate-${row.category}`}
                            >
                              {rate}%
                            </span>
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <ThumbsUp className="h-3.5 w-3.5" /> {row.got_it}
                            </span>
                            <span className="inline-flex items-center gap-1 text-gray-500">
                              <SkipForward className="h-3.5 w-3.5" /> {row.next}
                            </span>
                          </div>
                        </div>
                        {/* Tiny bar chart — width relative to the busiest category */}
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-all duration-700"
                            style={{ width: `${widthPct}%` }}
                            data-testid={`admin-insights-bar-${row.category}`}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </section>

            {/* Top acked tips */}
            <section data-testid="admin-insights-top-tips" className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Most-logged tips
              </h2>
              {data.top_acked_tips.length === 0 ? (
                <Card className="border-gray-100">
                  <CardContent className="p-5 text-sm text-gray-500">
                    No &ldquo;Got it&rdquo; clicks yet.
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-gray-100 shadow-sm rounded-2xl">
                  <CardContent className="p-0 divide-y divide-gray-100">
                    {data.top_acked_tips.map((t, i) => {
                      const m = metaFor(t.category);
                      return (
                        <div
                          key={`${t.category}-${i}`}
                          className="p-4 sm:p-5 flex items-start gap-3"
                          data-testid={`admin-insights-tip-${i}`}
                        >
                          <span
                            className={`shrink-0 inline-flex items-center justify-center h-7 px-2 rounded-full text-[11px] font-medium border ${m.accent}`}
                          >
                            {m.emoji} {m.label}
                          </span>
                          <p className="text-sm text-gray-800 leading-relaxed flex-1">
                            {t.tip_text || <span className="italic text-gray-400">(no text)</span>}
                          </p>
                          <span className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                            <ThumbsUp className="h-3.5 w-3.5" /> {t.got_it}
                          </span>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminInsights;
